import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Loader2, FileText, X } from 'lucide-react';
import { api } from '../lib/api';
import { useSections } from '../hooks/useSections';

interface SearchResult {
  id: string;
  sectionId: string;
  slug: string;
  parentChapterId: string | null;
  titleCs: string;
  snippet: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CommandPalette({ open, onClose }: Props) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const { data: secData } = useSections();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setQ('');
      setDebounced('');
      setCursor(0);
      // Focus na další tick, ať se modal nejdřív připojí do DOM.
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 150);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.get<{ results: SearchResult[] }>(`/chapters/search?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= 2,
  });

  const results = useMemo(() => data?.results ?? [], [data]);
  const sectionSlugById = useMemo(() => {
    const m = new Map<string, string>();
    (secData?.sections ?? []).forEach((s) => m.set(s.id, s.slug));
    return m;
  }, [secData]);

  useEffect(() => setCursor(0), [results.length]);

  const buildUrl = (r: SearchResult): string | null => {
    const sectionSlug = sectionSlugById.get(r.sectionId);
    if (!sectionSlug) return null;
    // Pro MVP nerozlišujeme top-level vs sub-chapter URL v hledání — sub má parent,
    // ale potřebovali bychom parent slug. Zatím navedeme na parent path, což v UI správně
    // vyřeší Chapter routing (zkusí najít top-level se slug=r.slug).
    return `/c/${sectionSlug}/${r.slug}`;
  };

  const handleSelect = (r: SearchResult) => {
    const url = buildUrl(r);
    if (url) {
      navigate(url);
      onClose();
    }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(0, results.length - 1)));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const r = results[cursor];
      if (r) handleSelect(r);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-dark/70 p-4 pt-[10vh] backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
      >
        <div className="flex items-center gap-3 border-b border-gray-200 px-5 py-4">
          {isFetching ? (
            <Loader2 className="h-5 w-5 animate-spin text-teal" />
          ) : (
            <Search className="h-5 w-5 text-gray-400" />
          )}
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Hledat kapitolu nebo obsah…"
            className="w-full border-0 bg-transparent text-base outline-none"
          />
          <kbd className="hidden rounded bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500 sm:inline">
            ESC
          </kbd>
          <button onClick={onClose} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-dark sm:hidden">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div ref={listRef} className="max-h-[60vh] overflow-y-auto">
          {debounced.length < 2 ? (
            <div className="px-5 py-10 text-center text-sm text-gray-500">
              Napiš aspoň 2 znaky. Výsledky se omezují na kapitoly, ke kterým máš přístup.
            </div>
          ) : results.length === 0 && !isFetching ? (
            <div className="px-5 py-10 text-center text-sm text-gray-500">
              Nic nenalezeno pro „{debounced}".
            </div>
          ) : (
            results.map((r, i) => {
              const active = i === cursor;
              const url = buildUrl(r);
              if (!url) return null;
              return (
                <button
                  key={r.id}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => handleSelect(r)}
                  className={
                    active
                      ? 'flex w-full items-start gap-3 bg-teal-light px-5 py-3 text-left'
                      : 'flex w-full items-start gap-3 px-5 py-3 text-left hover:bg-gray-50'
                  }
                >
                  <FileText className={active ? 'mt-0.5 h-4 w-4 shrink-0 text-teal' : 'mt-0.5 h-4 w-4 shrink-0 text-gray-400'} />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-semibold text-dark">{r.titleCs}</div>
                    {r.snippet && (
                      <div className="mt-0.5 truncate text-xs text-gray-500">
                        {highlight(r.snippet, debounced)}
                      </div>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-2 text-[11px] text-gray-400">
          <div className="flex gap-3">
            <span>
              <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono">↑↓</kbd> navigace
            </span>
            <span>
              <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono">Enter</kbd> otevřít
            </span>
          </div>
          <span>{results.length > 0 ? `${results.length} výsledků` : ''}</span>
        </div>
      </div>
    </div>
  );
}

function highlight(text: string, q: string): React.ReactNode {
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded bg-gold-soft px-0.5 font-semibold text-dark">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}
